/**
 * Collection Filter + Sort AJAX
 * - Sort select auto-submits its own form on change
 * - Filter forms intercept submit via capture phase
 * - Active-tag remove links intercepted via click
 * - All navigation replaces #collection-results, pushes URL
 */
(function () {
  'use strict';

  const RESULTS_ID = 'collection-results';
  let requestToken = 0;

  if (!document.getElementById(RESULTS_ID)) return;

  /* ---- Helpers ---- */

  function getResultsEl() { return document.getElementById(RESULTS_ID); }

  function setLoading(on) {
    const el = getResultsEl();
    if (!el) return;
    el.classList.toggle('cf-loading', on);
    el.setAttribute('aria-busy', String(on));
  }

  function scrollToResults() {
    const el = getResultsEl();
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
  }

  /* ---- Core AJAX fetch + replace ---- */

  function closeFilterUi() {
    const drawer = document.querySelector('[data-filter-drawer]');
    if (drawer && !drawer.hidden) {
      drawer.classList.remove('is-open');
      drawer.hidden = true;
      document.body.style.overflow = '';
    }
  }

  async function filterFetch(url, options = {}) {
    const { updateHistory = true } = options;
    const token = ++requestToken;
    setLoading(true);
    closeFilterUi();
    try {
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);

      const html = await res.text();
      if (token !== requestToken) return;

      const doc  = new DOMParser().parseFromString(html, 'text/html');

      const newResults = doc.getElementById(RESULTS_ID);
      const oldResults = getResultsEl();
      if (newResults && oldResults) {
        oldResults.replaceWith(newResults);
      }

      // Also swap the filter drawer so its checkboxes always reflect
      // the current filter state. Without this, clearing filters leaves
      // the drawer's old checkboxes ticked, which re-applies them on
      // the next filter selection.
      const newDrawer = doc.getElementById('filter-drawer');
      const oldDrawer = document.getElementById('filter-drawer');
      if (newDrawer && oldDrawer) {
        // Preserve open/hidden state — keep whatever the current drawer has
        newDrawer.hidden = true;
        newDrawer.classList.remove('is-open');
        oldDrawer.replaceWith(newDrawer);
      }

      if (updateHistory) history.pushState({}, '', url);
      scrollToResults();
      reinit();
    } catch (_) {
      window.location.href = url;
    } finally {
      if (token === requestToken) setLoading(false);
    }
  }

  /* ---- Build clean URL from a form ---- */

  // Collect price-filter input elements that have no value attribute set by the
  // server (i.e. the filter is not currently active). We check defaultValue: if
  // the server did not render a value="…" attribute, defaultValue is "" even if
  // the browser shows a "0" placeholder. We use this to avoid submitting
  // filter.p.m.price.lte=0 (which Shopify interprets as "price ≤ $0" → 0 results).
  function isPriceInputEmpty(form, name) {
    const el = form.querySelector('[data-price-filter][name="' + name + '"]');
    // defaultValue is "" when no value="" attribute was rendered by the server
    return el && el.defaultValue === '';
  }

  function buildUrlFromForm(form) {
    const currentUrl = new URL(window.location.href);
    const base = new URL(form.action || currentUrl.pathname, currentUrl.origin);
    const data = new FormData(form);
    const params = new URLSearchParams(currentUrl.search);
    const fieldNames = new Set();

    form.querySelectorAll('input[name], select[name], textarea[name]').forEach((field) => {
      fieldNames.add(field.name);
    });

    fieldNames.forEach((name) => params.delete(name));

    for (const [key, value] of data.entries()) {
      if (value === '') continue;
      // If a price input has no server-rendered value (filter not active) but the
      // browser is reporting "0" (placeholder default), skip it entirely so we
      // don't send filter.p.m.price.lte=0 and get zero results.
      if (isPriceInputEmpty(form, key)) continue;
      params.append(key, value);
    }

    params.delete('page');
    base.search = params.toString();
    return base.toString().replace(/\/page\/\d+(?=\/|$)/, '');
  }

  /* ---- Intercept: filter form submit ---- */

  document.addEventListener('submit', (e) => {
    // Sort form — handled separately via change event
    if (e.target.matches('[data-sort-form]')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      filterFetch(buildUrlFromForm(e.target));
      return;
    }
    // Filter form
    const form = e.target.closest('[data-cf-form]');
    if (!form) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    filterFetch(buildUrlFromForm(form));
  }, true);

  /* ---- Intercept: sort select change — auto-submit ---- */
  /* ---- Also: filter checkbox change — auto-submit parent filter form ---- */

  document.addEventListener('change', (e) => {
    // Sort select
    const select = e.target.closest('[data-cf-sort]');
    if (select) {
      const form = select.closest('[data-sort-form]') || select.closest('form');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      return;
    }

    // Filter checkbox (list type) — auto-submit so top-bar and sidebar work without an Apply button
    const checkbox = e.target.closest('input[type="checkbox"]');
    if (checkbox) {
      const form = checkbox.closest('[data-cf-form]');
      if (form) {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        return;
      }
    }
  });

  /* ---- Intercept: active-filter tag + clear-all clicks ---- */

  document.addEventListener('click', (e) => {
    const tag = e.target.closest('.cf__active-tag, .cf__clear-all, [data-cf-remove]');
    if (tag && tag.href) {
      e.preventDefault();
      // Keep the current sort_by when removing a filter
      const url = new URL(tag.href);
      const params = new URLSearchParams(url.search);
      if (!params.has('sort_by')) {
        const currentSort = new URLSearchParams(window.location.search).get('sort_by');
        if (currentSort) params.set('sort_by', currentSort);
      }
      url.search = params.toString();
      filterFetch(url.toString());
    }
  });

  /* ---- Handle browser back/forward ---- */

  window.addEventListener('popstate', () => {
    filterFetch(window.location.href, { updateHistory: false });
  });

  /* ---- Mobile sort sheet ---- */

  function getSortSheet() { return document.getElementById('SortSheet'); }

  function openSortSheet() {
    const sheet = getSortSheet();
    if (!sheet) return;
    sheet.removeAttribute('hidden');
    sheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => sheet.classList.add('is-open'));
  }

  function closeSortSheet() {
    const sheet = getSortSheet();
    if (!sheet) return;
    sheet.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => {
      sheet.setAttribute('hidden', '');
      sheet.setAttribute('aria-hidden', 'true');
    }, 300);
  }

  // Open on button click
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-sort-sheet-open]')) { openSortSheet(); return; }
    if (e.target.closest('[data-sort-sheet-close]')) { closeSortSheet(); return; }

    // Sort option selected in sheet
    const opt = e.target.closest('[data-sort-value]');
    if (!opt) return;
    const sortValue = opt.dataset.sortValue;
    if (!sortValue) return;
    closeSortSheet();
    // Build URL preserving all active filters, updating sort_by
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    url.searchParams.delete('page');
    filterFetch(url.toString());
  });

  // Keyboard: Escape closes
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { const s = getSortSheet(); if (s && !s.hasAttribute('hidden')) closeSortSheet(); }
  });

  /* ---- Re-initialise after DOM swap ---- */

  function announceFilterResults() {
    const countEl  = document.querySelector('[data-collection-count]');
    const statusEl = document.getElementById('collection-filter-status');
    if (!countEl || !statusEl) return;
    const msg = countEl.textContent.trim();
    statusEl.textContent = '';
    requestAnimationFrame(() => { statusEl.textContent = msg + ' shown'; });
  }

  function reinit() {
    announceFilterResults();
    // Wishlist active states
    if (typeof window._reinitWishlist === 'function') {
      window._reinitWishlist();
    } else {
      try {
        const list = JSON.parse(localStorage.getItem('af_wishlist')) || [];
        document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
          btn.classList.toggle('is-active', list.includes(btn.dataset.productHandle));
        });
      } catch (_) {}
    }

    // Top-bar dropdowns are fully event-delegated — no reinit needed

    // Column switcher: re-apply saved desktop/mobile col preference and
    // restore is-active state on the freshly-swapped toolbar buttons
    if (typeof window._reinitColSwitcher === 'function') window._reinitColSwitcher();

    // Load more
    if (typeof window._reinitLoadMore === 'function') window._reinitLoadMore();

    // Countdown timers
    if (typeof window._reinitCountdowns === 'function') window._reinitCountdowns();
    // Card sliders use event delegation — no reinit needed
  }

})();
